/**
 * SyncToBcModal. Prompts for one posting date (default today), then syncs the
 * selected BC-eligible invoices to Business Central via
 * POST /v1/documents/invoices/sync with destination 'bc'.
 *
 * Unlike Sage (which only generates a CSV), BC writes directly: each covered
 * invoice becomes a DRAFT purchaseInvoice in BC (header + a G/L account line).
 * A human in BC reviews and posts it. While the server flag INVOICE_BC_SYNC_ENABLED
 * is off, the call returns { status:'disabled' } and writes nothing; the modal
 * shows that plainly. A selection with no BC-eligible rows returns 422.
 */

import { useState } from 'react';
import { Loader2, Upload, X } from 'lucide-react';
import { useSyncInvoices } from '@/lib/useInvoices';

// Today as YYYY-MM-DD in the browser's local time (matches an <input type=date>).
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Pull the server's human-readable message out of an api error body
// ({ error: { code, message } }), falling back to the thrown message.
function serverMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'error' in body) {
      const e = (body as { error?: { message?: unknown } }).error;
      if (e && typeof e.message === 'string') return e.message;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

type Props = {
  invoiceIds: string[];
  onClose: () => void;
  onSynced?: () => void;
};

type Outcome =
  | { kind: 'ok'; count: number }
  | { kind: 'disabled' };

export function SyncToBcModal({ invoiceIds, onClose, onSynced }: Props) {
  const [postingDate, setPostingDate] = useState(todayLocal());
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const sync = useSyncInvoices();
  const errorMsg = sync.error ? serverMessage(sync.error) : null;

  const onRun = () => {
    sync.mutate(
      { destination: 'bc', invoice_ids: invoiceIds, posting_date: postingDate },
      {
        onSuccess: (res) => {
          if (res.destination !== 'bc') return;
          // Discriminate on `count`, present only on the enabled (ok) result;
          // the disabled result carries `status: 'disabled'` instead.
          if ('count' in res) {
            setOutcome({ kind: 'ok', count: res.count });
            onSynced?.();
          } else {
            setOutcome({ kind: 'disabled' });
          }
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-5">
          <h2 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">Sync to Business Central</h2>
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-4">
            Creates a draft vendor invoice in Business Central for the {invoiceIds.length} selected invoice{invoiceIds.length === 1 ? '' : 's'}. Each draft is left unposted so Accounting reviews and posts it in BC. Only BC-matched invoices that are Not in ERP are included.
          </p>

          {outcome?.kind === 'ok' ? (
            <div className="space-y-3">
              <p className="text-sm text-green-700 dark:text-green-300">
                Created {outcome.count} draft invoice{outcome.count === 1 ? '' : 's'} in Business Central. Review and post them in BC.
              </p>
              <div className="pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Done
                </button>
              </div>
            </div>
          ) : outcome?.kind === 'disabled' ? (
            <div className="space-y-3">
              <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
                Business Central sync is not enabled yet. Nothing was written to BC. The selection was recorded.
              </p>
              <div className="pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label htmlFor="bc-posting-date" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
                  Posting date
                </label>
                <input
                  id="bc-posting-date"
                  type="date"
                  value={postingDate}
                  onChange={(e) => setPostingDate(e.target.value)}
                  className="w-full border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                />
                <p className="mt-1 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Applied to every draft in this push.
                </p>
              </div>

              {errorMsg && <p className="text-xs text-red-700 dark:text-red-300">{errorMsg}</p>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onRun}
                  disabled={sync.isPending || !postingDate}
                  className="inline-flex items-center gap-1 text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
                >
                  {sync.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                  {sync.isPending ? 'Creating drafts...' : 'Create BC drafts'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
